import { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import { StaffSearchResult } from '../../../types/api/organizationStaff';
import PrimaryBlueButton from '../../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../../components/common/buttons/BorderedButton';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type AddStaffModalProps = {
  open: boolean;
  searchResults: StaffSearchResult[];
  onClose: () => void;
  onSearchInputChange: (value: string) => void;
  onCandidateChange: (candidate: StaffSearchResult | null) => void;
  onRoleChange: (event: SelectChangeEvent<string>) => void;
  onAddStaff: () => void;
  onSendInvite: () => void;
  inputValue: string;
  isSearching: boolean;
  hasSearched: boolean;
  searchError: string | null;
  dialogMessage: { type: 'success' | 'error'; text: string } | null;
  roles: string[];
  selectedRole: string;
  isRolesLoading: boolean;
  rolesError: string | null;
  isAddingStaff: boolean;
  isInviting: boolean;
  selectedCandidate: StaffSearchResult | null;
  searchDropdownOpen?: boolean;
};

function AddStaffModal({
  open,
  searchResults,
  onClose,
  onSearchInputChange,
  onCandidateChange,
  onRoleChange,
  onAddStaff,
  onSendInvite,
  inputValue,
  isSearching,
  hasSearched,
  searchError,
  dialogMessage,
  roles,
  selectedRole,
  isRolesLoading,
  rolesError,
  isAddingStaff,
  isInviting,
  selectedCandidate,
  searchDropdownOpen,
}: AddStaffModalProps): JSX.Element {
  const trimmedInput = inputValue.trim();
  const isEmail = useMemo(() => EMAIL_REGEX.test(trimmedInput), [trimmedInput]);
  const canSendInvite = isEmail;

  useEffect(() => {
    if (!open) return;

    if (!selectedCandidate && isEmail && searchResults.length === 1) {
      onCandidateChange(searchResults[0]);
    }
  }, [open, onCandidateChange, searchResults, selectedCandidate, isEmail]);

  const autoOpenProps = searchDropdownOpen ? ({ open: true as const } as const) : ({} as const);

  const noOptionsText = useMemo(() => {
    if (!trimmedInput) return 'Enter an email to search';
    if (!isEmail) return 'Enter a valid email address to search';
    if (hasSearched) return 'No matching users found';
    return 'Start typing to search';
  }, [trimmedInput, isEmail, hasSearched]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Staff</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          Search for an existing user by email or invite a new teammate by email.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Autocomplete
            {...autoOpenProps}
            value={selectedCandidate}
            inputValue={inputValue}
            onInputChange={(_, newValue, reason) => {
              if (reason !== 'input' && reason !== 'clear') return;

              const v = (newValue ?? '').trim();
              const emailValid = EMAIL_REGEX.test(v);

              onCandidateChange(null);
              onSearchInputChange(emailValid ? v : newValue ?? '');
            }}
            onChange={(_, newValue) => {
              if (!newValue || typeof newValue === 'string') {
                onCandidateChange(null);
                return;
              }
              onCandidateChange(newValue);
            }}
            options={searchResults}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.full_name || option.email || option.user_id
            }
            loading={isSearching}
            disabled={isRolesLoading || isAddingStaff}
            loadingText="Searching..."
            noOptionsText={noOptionsText}
            renderOption={(props, option) => (
              <li {...props} key={option.user_id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1">{option.full_name || option.email || option.user_id}</Typography>
                  {option.email && (
                    <Typography variant="body2" color="text.secondary">
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by email"
                placeholder="Type to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {selectedCandidate && (
          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => theme.palette.action.hover,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {selectedCandidate.full_name}
              </Typography>
              {selectedCandidate.email && (
                <Typography variant="body2" color="text.secondary">
                  {selectedCandidate.email}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => {
                onCandidateChange(null);
                onSearchInputChange('');
              }}
              disabled={isAddingStaff}
            >
              Remove
            </Button>
          </Box>
        )}

        {searchError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {searchError}
          </Alert>
        )}

        {hasSearched && !isSearching && !searchResults.length && !searchError && isEmail && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No matching users found. Send them an invite instead.
          </Typography>
        )}

        <FormControl fullWidth sx={{ mt: 3 }} disabled={isRolesLoading || !roles.length || isAddingStaff}>
          <InputLabel id="staff-role-label">Role</InputLabel>
          <Select labelId="staff-role-label" value={selectedRole} label="Role" onChange={onRoleChange}>
            {roles.map((role) => (
              <MenuItem key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isRolesLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading ...
            </Typography>
          </Box>
        )}

        {rolesError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {rolesError}
          </Alert>
        )}

        {dialogMessage && (
          <Alert severity={dialogMessage.type} sx={{ mt: 2 }}>
            {dialogMessage.text}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& > *': {
            mt: { xs: 1.5, sm: 0 },
          },
        }}
      >
        <BorderedButton
          onClick={onSendInvite}
          buttonText={isInviting ? 'Sending...' : 'Send Invite'}
          sxProps={{
            height: 42,
            minWidth: 140,
            px: 2.5,
          }}
          additionalProps={{ disabled: !canSendInvite || isInviting || isAddingStaff || Boolean(selectedCandidate) }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <BorderedButton
          onClick={onClose}
          buttonText="Cancel"
          sxProps={{
            height: 42,
            minWidth: 120,
            px: 2.5,
          }}
          additionalProps={{ disabled: isAddingStaff }}
        />
        <PrimaryBlueButton
          onClick={onAddStaff}
          sxProps={{
            height: 42,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 150,
            px: 2.5,
          }}
          additionalProps={{ disabled: isAddingStaff || !selectedCandidate || !selectedRole }}
          buttonText={isAddingStaff ? 'Adding...' : 'Add Staff'}
        />
      </DialogActions>
    </Dialog>
  );
}

export default AddStaffModal;
